import { Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { FC, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { payrollSelectAllUserDates, payrollSetFilter } from "actions/payrollAction";
import { EnchancedSortLabel } from "components/tables/tableSortLabel";
import useResize from "hooks/useResize";
import { areAllPayrollRowsSelected, getFilteredPayrollRows } from "utils/payrollHelpers";
import { PAYROLL_TABLE_COLS } from "./payrollTable";
import { PayrollRow } from "./PayrollRow";
import { useStyles } from "./styles";

interface PropsType {
  tableNumber: number;
  page: number;
  rowsPerPage: number;
}

const PayrollTable: FC<PropsType> = ({ tableNumber, page, rowsPerPage }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { width } = useResize();
  const isMobile = width < 840;
  const { dataList, payrollFilters, selctedUserDatesList } = useTypeSelectors(
    (state) => state.payrollTable,
  );
  const sortId = payrollFilters.ordering || "user";

  const rows = useMemo(
    () =>
      getFilteredPayrollRows(
        dataList,
        sortId,
        payrollFilters.search,
        payrollFilters.station,
        payrollFilters.userRole,
        payrollFilters.userStatus,
        payrollFilters.user?.id,
      ),
    [
      dataList,
      sortId,
      payrollFilters.search,
      payrollFilters.station,
      payrollFilters.userRole,
      payrollFilters.userStatus,
      payrollFilters.user?.id,
    ],
  );

  const pagedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage],
  );

  const handleSortChange = (currentSort: string | undefined, cellId: string) => {
    const nextSort =
      currentSort?.replace("-", "") === cellId
        ? `${currentSort?.includes("-") ? "" : "-"}${cellId}`
        : cellId;
    dispatch(payrollSetFilter({ ordering: nextSort }));
  };

  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {tableNumber === 1 && (
            <TableCell className={`${classes.tableHeadCheckCell} ${classes.fixedHeight}`}>
              <Checkbox
                onChange={() => dispatch(payrollSelectAllUserDates(pagedRows))}
                checked={areAllPayrollRowsSelected(selctedUserDatesList, pagedRows)}
                className={classes.selectCheckbox}
              />
            </TableCell>
          )}
          {PAYROLL_TABLE_COLS.filter((col) =>
            isMobile ? tableNumber === 2 : col.tableNumber === tableNumber,
          ).map((col) => (
            <TableCell key={col.id} className={`${classes.tableHeadCell} ${classes.fixedHeight}`}>
              <Box className={classes.tableCellContent}>
                <EnchancedSortLabel
                  columnId={col.id}
                  columnLabel={col.label}
                  hasSortLabel={col.hasSort}
                  handleChange={handleSortChange}
                  ordering={sortId}
                />
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {pagedRows.map((row, index) => (
          <PayrollRow
            key={`${row.user_id}-${row.date}-${row.station_id}-${row.role}-${index}`}
            row={row}
            tableNumber={tableNumber}
            isMobile={isMobile}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default PayrollTable;
